import { useRef } from "react";
import { ArrowRight, Globe, TrendingUp, Building2 } from "lucide-react";
import { motion, ease as mmEase, useInView } from "@/lib/motion";

const ez = mmEase.expo;

const B_FEATURES = [
  {
    icon: Globe,
    title: "Batch payments",
    desc: "Upload CSV. Pay 1,000 contractors in one click across 45 countries.",
  },
  {
    icon: TrendingUp,
    title: "Treasury dashboard",
    desc: "Real-time FX exposure, liquidity management, and automated rate alerts.",
  },
  {
    icon: Building2,
    title: "Multi-user access",
    desc: "Role-based permissions for your finance team with a full audit trail.",
  },
];

const CHART_BARS = [40, 62, 45, 78, 55, 88, 70, 85, 60, 92, 74, 100];

const FX_ROWS = [
  { label: "GBP / NGN", amount: "£84,200", pct: "+0.3%", c: "var(--color-success)" },
  { label: "USD / GHS", amount: "$41,900", pct: "-0.1%", c: "#6366F1" },
  { label: "EUR / KES", amount: "€29,400", pct: "+0.5%", c: "#06B6D4" },
];

export default function BusinessSection({ goToApp }) {
  const ref    = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px 0px" });

  return (
    <section id="business" ref={ref} className="relative py-24 px-6 section-dark overflow-hidden">

      <div className="relative z-10 max-w-[1200px] mx-auto">
        <div className="grid lg:grid-cols-2 gap-14 items-center">

          {/* Left */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.55, ease: ez }}
            >
              <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-emerald mb-5">
                For businesses
              </p>
              <h2
                className="font-display font-black text-white text-balance leading-[1.08] mb-6"
                style={{ fontSize: "clamp(1.75rem, 3.5vw, 2.75rem)", letterSpacing: "-0.035em" }}
              >
                Your payroll shouldn't stop because your team lives in five countries.
              </h2>
              <p
                className="text-[1rem] leading-relaxed mb-10"
                style={{ color: "rgba(255,255,255,0.44)" }}
              >
                MoneyMeet Pay for Business gives you multi-user access, batch payments, API
                integration, real-time FX management, and a dedicated account manager.
              </p>
            </motion.div>

            <div className="space-y-6 mb-10">
              {B_FEATURES.map((f, i) => (
                <motion.div
                  key={f.title}
                  initial={{ opacity: 0, x: -16 }}
                  animate={inView ? { opacity: 1, x: 0 } : {}}
                  transition={{ duration: 0.5, ease: ez, delay: 0.1 + i * 0.08 }}
                  className="flex items-start gap-4"
                >
                  <div
                    className="w-10 h-10 rounded-xl grid place-items-center shrink-0 mt-0.5"
                    style={{
                      background: "color-mix(in srgb, var(--color-success) 12%, transparent)",
                      border: "1px solid color-mix(in srgb, var(--color-success) 20%, transparent)",
                    }}
                  >
                    <f.icon size={17} className="text-emerald" strokeWidth={1.8} />
                  </div>
                  <div>
                    <p className="font-bold text-white text-[14px] mb-0.5">{f.title}</p>
                    <p className="text-[13px]" style={{ color: "rgba(255,255,255,0.38)" }}>
                      {f.desc}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>

            <div className="flex flex-wrap gap-3">
              <button
                onClick={goToApp}
                className="btn-primary inline-flex items-center gap-2 px-6 py-3 rounded-full text-[13px] font-bold"
              >
                Open business account <ArrowRight size={13} strokeWidth={2.5} />
              </button>
              <button
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-[13px] font-semibold"
                style={{ border: "1px solid rgba(255,255,255,0.14)", color: "rgba(255,255,255,0.50)", transition: "border-color 0.2s ease, color 0.2s ease" }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.28)"; e.currentTarget.style.color = "rgba(255,255,255,0.72)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.14)"; e.currentTarget.style.color = "rgba(255,255,255,0.50)"; }}
              >
                Talk to sales
              </button>
            </div>
          </div>

          {/* Right — treasury mockup */}
          <motion.div
            initial={{ opacity: 0, scale: 0.93 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.68, ease: ez, delay: 0.15 }}
            className="card-dark p-6"
          >
            <div className="flex items-center justify-between mb-6">
              <div>
                <p
                  className="text-[10px] uppercase tracking-[0.2em] mb-1"
                  style={{ color: "rgba(255,255,255,0.28)" }}
                >
                  Treasury overview
                </p>
                <p className="font-display font-black text-white text-xl num-tabular">
                  $2,847,392.40
                </p>
              </div>
              <span
                className="text-[10px] font-bold px-3 py-1.5 rounded-full text-success bg-success/10"
              >
                ↑ 12.4% this month
              </span>
            </div>

            {/* Chart bars */}
            <div className="flex items-end gap-1.5 h-20 mb-6">
              {CHART_BARS.map((h, i) => (
                <div
                  key={i}
                  className="flex-1 rounded-sm"
                  style={{
                    height: `${h}%`,
                    background: i === CHART_BARS.length - 1 ? "var(--color-success)" : "rgba(255,255,255,0.08)",
                  }}
                />
              ))}
            </div>

            {FX_ROWS.map((row) => (
              <div
                key={row.label}
                className="flex items-center justify-between py-3"
                style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}
              >
                <p className="text-[12px] font-semibold" style={{ color: "rgba(255,255,255,0.52)" }}>
                  {row.label}
                </p>
                <p className="text-[12px] font-bold num-tabular" style={{ color: "rgba(255,255,255,0.80)" }}>
                  {row.amount}
                </p>
                <span className="text-[11px] font-semibold" style={{ color: row.c }}>
                  {row.pct}
                </span>
              </div>
            ))}
          </motion.div>

        </div>
      </div>
    </section>
  );
}
